// @flow
import React, {useContext, useEffect, useState} from "react"
import LIUtils from "../GUI_COMMON/SUPPORT/LIUtils";
import {StoreContext} from "../GUI_COMMON/SUPPORT/store";
import Header from "./Header";
import BackButton from "../GUI_COMMON/COMPONENTS/BackButton";
import ArchiverClientApi from "../API/archiverClientApi";
import {useParams} from "react-router-dom";
import {clearAllCaseInfo, clearCaseHistory, gotCaseBasicInfo, gotCaseHistory} from "../ACTIONS/clientActions";
import LoadingSpinner from "../GUI_COMMON/COMPONENTS/LoadingSpinner";
import {useCookies} from "react-cookie";
import ArchiverGuiUtils from "../SUPPORT/ArchiverGuiUtils";
import Popup from "../GUI_COMMON/COMPONENTS/Popup";
import ReactDOMServer from "react-dom/server";
import GuiUtils from "../GUI_COMMON/SUPPORT/GuiUtils";
import {useHistory} from "react-router";
import {SampleBadgeOut} from "./SampleBadgeOut";
import {SampleBadgeOffPremises} from "./SampleBadgeOffPremises";
import {SampleBadgeDeleted} from "./SampleBadgeDeleted";
import {SampleBadgeIn} from "./SampleBadgeIn";

const CaseDetailsPage = () => {

    const [globalState,dispatch] = useContext(StoreContext);
    const [isSearchingHistory,setIsSearchingHistory] = useState(false);
    const [isSearchingBasic,setIsSearchingBasic] = useState(false);

    const { caseNum } = useParams();
    const [caseId, setCaseId] = useState(-1);
    const [cookies] = useCookies();
    const history = useHistory();

    useEffect(() => {
        console.debug("ShowCaseDetailsPage starting up...");

        window.scrollTo(0,0);
        LIUtils.setBackgroundColor('--li-main-bg-color');
        getCaseBasicInfo(caseNum);
    }, [caseNum]);  // eslint-disable-line react-hooks/exhaustive-deps

    function getCaseBasicInfo(caseNum) {
        // if(globalState.case.id === caseId) { // no need to refresh
        //     return;
        // }
        setIsSearchingBasic(true);
        dispatch(clearAllCaseInfo());
        ArchiverClientApi.getCaseBaseInfo(caseNum)
        .then( (caseInfo) => {
            setCaseId(caseInfo.id); // trap this for later
            dispatch(gotCaseBasicInfo(caseInfo.id,caseInfo));  // case id comes from here
            getCaseHistory(caseInfo.id); // now grab the history
        })
        .catch( responseError => ArchiverGuiUtils.ShowErrorIfNotUnauthourized(responseError))
        .finally( () => setIsSearchingBasic(false));
    }

    function getCaseHistory(caseId) {

        setIsSearchingHistory(true);
        dispatch(clearCaseHistory());
        ArchiverClientApi.getCaseHistory(caseId)
        .then( (caseHistory) => {
            dispatch(gotCaseHistory(caseId,caseHistory));
        })
        .catch( responseError => ArchiverGuiUtils.ShowErrorIfNotUnauthourized(responseError))
        .finally( () => setIsSearchingHistory(false));
    }

    function handleShowInventory() {
        history.push("/case_inventory/"+caseId+"/"+caseNum);
    }

    function showBasicInfo() {
        if(isSearchingBasic) {
            return <LoadingSpinner medium center/>;
        }
        return(
            <div className="row p-0 m-0 mt-3 justify-content-between">
                    <div className="ml-5 li-font-medium" style={{width:"500px"}}>
                        <div><strong>Case Started:</strong> {LIUtils.shortDateFromJsonUtcDateStr(globalState.case.dateStarted)}</div>
                        {dumpCaseInOutInfo()}
                    </div>
                <div className="mt-3">
                    <button className="li-button li-primary" onClick={handleShowInventory}>Show Inventory</button>
                </div>
            </div>
        );
    }
    
    function dumpCaseInOutInfo() {
        if(!globalState.case.inOutItems) {
            return null;
        }
        
        const sampleList = globalState.case.inOutItems.map((item) => {
            const name = GuiUtils.GetSampleTypeNameGivenId(item.sampleType,globalState.sampleTypes.items);
            return <tr key={item.sampleType}>
                <td className="font-weight-bold">{name}</td>
                <td>{item.numSamples}</td>
                <td>{item.numSamples-item.numSamplesOut} / {item.numSamplesOut}</td>
                <td>{item.numSamplesOffPremises??"-"}</td>
                <td>{item.numSamplesDeleted??"-"}</td>
            </tr>;
        });
        
        return (
            <div className="mt-4">
                <table className="table table-sm li-font-med-small ">
                    <thead></thead>
                    <tbody className="text-center">
                    <tr><th></th><th>Total #</th><th><SampleBadgeIn/>&nbsp;&nbsp;/<SampleBadgeOut/></th><th><SampleBadgeOffPremises/></th><th><SampleBadgeDeleted/></th></tr>
                {sampleList}
                </tbody>
                </table>
            </div>
        );
    }

    function handleSampleClicked(sampleId) {
        history.push(`/sample_details/${sampleId}`);
    }

    function historyLinkClickHandler(note: Element, sampleId: number) {
        if(!note) return;
        ArchiverClientApi.sendNotifySampleLocationViewed(sampleId,"Case Details"); // fire and forget
        Popup.showInfo(ReactDOMServer.renderToString(note), true);
    }

    function showHistoryTable() {
        if(!globalState.case) return;

        const caseHistory = globalState.case.history;

        if (globalState.case.id===-1 || caseHistory.length === 0) {
            return <div className="card">{isSearchingHistory?<LoadingSpinner center medium/>:"No history found"}</div>;
        }

        const resultList = caseHistory.map((item, index) => {
            
                const historyNote = LIUtils.translateHistoryNote(item.note, LIUtils.UserCanSeeSampleLocation(cookies), (note)=>historyLinkClickHandler(note,item.sampleId));
                
                return <tr className="text-left" key={item.id}>
                    <td>{LIUtils.shortDateTimeFromJsonUtcDateStr(item.date)}</td>
                    <td className="text-center"><span className="btn-link li-pointer"
                                                      onClick={() => handleSampleClicked(item.sampleId)}>{item.sampleNumber}</span>
                    </td>
                    <td width="250px">{item.actionVerb}</td>
                    <td>{item.actor}</td>
                    <td style={{whiteSpace: 'pre-line'}}>
                        {historyNote}
                    </td>
                </tr>
            }
        );

        return (
            <div className="li-scrollable-at-500">
                <table className="table table-striped">
                    <thead>
                    <tr className="text-left"><th>Date</th><th width="60px">Sample</th><th className="50px text-wrap">Action</th><th>Who</th><th>Note</th></tr>
                    </thead>
                    <tbody>
                    {resultList}
                    </tbody>
                </table>
            </div>
        );
    }
    
    return (
        <div className="container">
            <Header/>
            <div className="row m-0 p-0 li-header-row justify-content-between">
                <div>
                    <BackButton/>
                    <h1 className="m-0 p-0 d-inline-block align-middle">Case: 
                        <span className="li-fg-primary ml-2">{caseNum}</span> 
                    </h1>
                </div>
                <div className="li-font-small text-right my-auto ml-auto mr-3">[id: {globalState.case.id}]</div>
            </div>
            
            {showBasicInfo()}
            <div className="mb-5">
                <div className="li-font-huge">History</div>
                <div className="li-search-results-table">
                    {showHistoryTable()}
                </div>
            </div>
        </div>
    );
}

export default CaseDetailsPage;
